import { DISCOVER_API_URI } from 'config';
import { calculatePercentage } from 'src/helpers/math';
import { get } from './common';

const CATEGORY_EVERYTHING_ID = 999;
const PAGE_SIZE = 12;

interface Creator {
  FacebookId: number;
  FirstName: string;
  UserGuid: string;
}

interface DicoveryPage {
  ActivityDate: string;
  AmountRaised: number;
  AskId: string;
  AskType: string;
  CardVersion: number;
  CardType: string;
  Cause: any;
  Creator: Creator;
  PhotoDetails?: {
    CropHeight: number;
    CropLeft: number;
    CropTop: number;
    CropWidth: number;
    Height: number;
    Width: number;
  };
  PhotoUrl: string;
  TargetAmount: number;
  TargetCurrency: Currency;
  Title: string;
  TotalDonations: number;
  Url: string;
  What: string;
}

interface DiscoveryResponse {
  Page: number;
  TotalPages: number;
  TotalResults: number;
}

interface PromotedResponse extends DiscoveryResponse {
  _embedded: {
    promoted: DicoveryPage[];
  };
}

interface PageNearYouResponse extends DiscoveryResponse {
  _embedded: {
    nearyou: DicoveryPage[];
  };
}

interface PopularResponse extends DiscoveryResponse {
  _embedded: {
    popular: DicoveryPage[];
  };
}

interface GetPromotedPages {
  items: MappedProject[];
}

export async function getPromotedPages(): Promise<GetPromotedPages> {
  const resourceUrl = createResourceUrl('promoted', 1);
  const result = await get<PromotedResponse>(resourceUrl, {
    baseUri: DISCOVER_API_URI,
    timingName: 'promotedPages',
    autoRefreshAccessToken: false,
  });

  if (!result || !result._embedded || !result._embedded.promoted) {
    return {
      items: [],
    };
  }

  return {
    items: result._embedded.promoted.map(mapCardToProject),
  };
}

interface GetPopularPages {
  totalCount: number;
  pageNo: number;
  items: MappedProject[];
}

export async function getPopularPages(
  pageNo: number,
  categoryId: number
): Promise<GetPopularPages> {
  const resourceUrl = createResourceUrl('popular', pageNo, categoryId);
  const result = await get<PopularResponse>(resourceUrl, {
    baseUri: DISCOVER_API_URI,
    timingName: 'popularPages',
    autoRefreshAccessToken: false,
  });

  if (!result || !result._embedded || !result._embedded.popular) {
    return {
      totalCount: 0,
      pageNo: 0,
      items: [],
    };
  }

  return {
    totalCount: result.TotalResults,
    pageNo: result.Page,
    items: result._embedded.popular.map(mapCardToProject),
  };
}

interface GetPagesNearYou {
  totalCount: number;
  pageNo: number;
  items: any[];
}

export async function getPagesNearYou(
  pageNo: number,
  categoryId: number
): Promise<GetPagesNearYou> {
  const resourceUrl = createResourceUrl('nearyou', pageNo, categoryId);
  const result = await get<PageNearYouResponse>(resourceUrl, {
    baseUri: DISCOVER_API_URI,
    timingName: 'pagesNearYou',
    autoRefreshAccessToken: false,
  });

  if (!result || !result._embedded || !result._embedded.nearyou) {
    return {
      totalCount: 0,
      pageNo: 0,
      items: [],
    };
  }

  // HACK: Total count is not calculated correctly by the discovery API
  // so it needs to be set manually in order to stop loading more items.
  const count = result._embedded.nearyou.length;

  return {
    totalCount: count >= PAGE_SIZE ? result.TotalResults : count,
    pageNo: result.Page,
    items: result._embedded.nearyou.map(mapCardToProject),
  };
}

export async function getActivePagesCount(): Promise<number> {
  const result = await get<{
    Count: number;
  }>('v1/stats/active?askType=Project', {
    baseUri: DISCOVER_API_URI,
    timingName: 'activePagesCount',
    autoRefreshAccessToken: false,
  });

  return result!.Count;
}

function createResourceUrl(
  resource: string,
  pageNo: number,
  categoryId?: number
): string {
  const resourceUrl = `v1/entities/${resource}?askType=Project&page=${pageNo}`;

  return typeof categoryId !== 'number' || categoryId === CATEGORY_EVERYTHING_ID
    ? resourceUrl
    : resourceUrl.concat(`&categoryId=${categoryId}`);
}

export interface MappedProject {
  targetCurrency: Currency;
  amountRaised: number;
  progress: number;
  pitch: string;
  owner: string;
  numberOfSupporters: number;
  ownerId: string;
  projectImage: {
    url: string;
    width?: number;
    height?: number;
    cropArea?: {
      left: number;
      top: number;
      width: number;
      height: number;
    };
  };
  projectId: string;
  projectUrl: string;
  projectType: string;
}

function mapCardToProject(card: DicoveryPage): MappedProject {
  const page: any = {
    targetCurrency: card.TargetCurrency,
    amountRaised: card.AmountRaised,
    progress: calculatePercentage(card.AmountRaised, card.TargetAmount),
    pitch: card.What,
    owner: card.Creator.FirstName,
    numberOfSupporters: card.TotalDonations,
    ownerId: card.Creator.UserGuid,
    projectImage: {
      url: card.PhotoUrl,
    },
    projectId: card.AskId,
    projectUrl: card.Url,
    projectType: card.CardType,
  };

  if (card.PhotoDetails) {
    page.projectImage = {
      ...page.projectImage,
      width: card.PhotoDetails.Width,
      height: card.PhotoDetails.Height,
      cropArea: {
        left: card.PhotoDetails.CropLeft,
        top: card.PhotoDetails.CropTop,
        width: card.PhotoDetails.CropWidth,
        height: card.PhotoDetails.CropHeight,
      },
    };
  }

  return page;
}



// WEBPACK FOOTER //
// ./src/helpers/api/discovery.ts